/**
 * VARIANT IMAGE AUTO-SELECTION - MOBILE + DESKTOP VERSION
 * For Legends in Thread - Impulse Theme
 * 
 * WHAT THIS DOES:
 * - DESKTOP: When customer CLICKS a thumbnail → auto-selects that color
 * - MOBILE: When customer SWIPES through images → auto-selects that color
 * 
 * HOW IT WORKS:
 * 1. Desktop: Customer clicks WHITE t-shirt thumbnail → "White" auto-selected
 * 2. Mobile: Customer swipes to WHITE t-shirt → "White" auto-selected
 * 3. Both cases: Customer can immediately add to cart!
 * 
 * UPDATED: Now includes mobile swipe/slide detection
 */

(function() {
  'use strict';
  
  // Wait for page to fully load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  
  function init() {
    console.log('🎨 Variant Auto-Select (Mobile + Desktop): Initializing...');
    
    // Get all the variants data from the page
    const variantsData = getProductVariants();
    
    if (!variantsData || variantsData.length === 0) {
      console.log('❌ No product variants found');
      return;
    }
    
    console.log(`✅ Found ${variantsData.length} variants`);
    
    // DESKTOP: Setup click handlers for thumbnail images
    setupThumbnailClicks(variantsData);
    
    // MOBILE: Setup swipe/slide detection for main image gallery
    setupMobileSwipeDetection(variantsData);
    
    console.log('✅ Variant Auto-Select: Ready for both desktop and mobile!');
  }
  
  /**
   * DESKTOP: Setup click handlers for thumbnail images
   */
  function setupThumbnailClicks(variantsData) {
    const thumbnails = findThumbnailImages();
    
    if (thumbnails.length === 0) {
      console.log('⚠️ No thumbnail images found for desktop clicks');
      return;
    }
    
    console.log(`✅ Found ${thumbnails.length} thumbnail images for desktop`);
    
    thumbnails.forEach(function(thumb) {
      thumb.addEventListener('click', function(e) {
        handleImageInteraction(thumb, variantsData, 'click');
      });
      thumb.style.cursor = 'pointer';
    });
  }
  
  /**
   * MOBILE: Setup swipe/slide detection for image gallery
   */
  function setupMobileSwipeDetection(variantsData) {
    // Find the main product image container/slider
    const sliderSelectors = [
      '.product-single__photos',
      '.product__photos',
      '.product-gallery',
      '.product__main-photos',
      '[data-product-single-media-wrapper]',
      '.flickity-viewport', // Common slider library
      '.slick-track', // Another common slider
      '.product-images-slider'
    ];
    
    let slider = null;
    for (let i = 0; i < sliderSelectors.length; i++) {
      slider = document.querySelector(sliderSelectors[i]);
      if (slider) {
        console.log('✅ Found mobile slider:', sliderSelectors[i]);
        break;
      }
    }
    
    if (!slider) {
      console.log('⚠️ No mobile slider found - trying alternative method');
      // Alternative: watch all large product images
      setupImageObserver(variantsData);
      return;
    }
    
    // Method 1: Listen for Flickity/Slick slider change events
    slider.addEventListener('change', function(e) {
      console.log('📱 Mobile: Slider changed');
      handleSliderChange(slider, variantsData);
    });
    
    // Method 2: Observe when active/selected class changes
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.attributeName === 'class' || 
            mutation.attributeName === 'aria-hidden' ||
            mutation.attributeName === 'data-active') {
          console.log('📱 Mobile: Image visibility changed');
          handleSliderChange(slider, variantsData);
        }
      });
    });
    
    // Observe all images in the slider
    const sliderImages = slider.querySelectorAll('img');
    sliderImages.forEach(function(img) {
      observer.observe(img.parentElement, {
        attributes: true,
        attributeFilter: ['class', 'aria-hidden', 'data-active', 'style']
      });
    });
    
    // Method 3: Touch/scroll events as fallback
    let scrollTimeout;
    slider.addEventListener('scroll', function() {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(function() {
        console.log('📱 Mobile: Scroll ended');
        handleSliderChange(slider, variantsData);
      }, 150);
    });
    
    slider.addEventListener('touchend', function() {
      setTimeout(function() {
        console.log('📱 Mobile: Touch ended');
        handleSliderChange(slider, variantsData);
      }, 200);
    });
  }
  
  /**
   * Alternative: Watch for image visibility changes using Intersection Observer
   */
  function setupImageObserver(variantsData) {
    const allProductImages = document.querySelectorAll('img[src*="mockup"]');
    
    if (allProductImages.length === 0) {
      console.log('⚠️ No product images found');
      return;
    }
    
    console.log(`✅ Watching ${allProductImages.length} images with Intersection Observer`);
    
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5 // Image must be 50% visible
    };
    
    const observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          console.log('📱 Mobile: Image became visible');
          handleImageInteraction(entry.target, variantsData, 'swipe');
        }
      });
    }, observerOptions);
    
    allProductImages.forEach(function(img) {
      observer.observe(img);
    });
  }
  
  /**
   * Handle slider/swipe change events
   */
  function handleSliderChange(slider, variantsData) {
    // Find the currently visible/active image
    const activeImage = findActiveImage(slider);
    
    if (activeImage) {
      handleImageInteraction(activeImage, variantsData, 'swipe');
    }
  }
  
  /**
   * Find which image is currently active/visible in the slider
   */
  function findActiveImage(slider) {
    // Try different methods to find the active slide
    
    // Method 1: Look for active/selected class
    const activeSelectors = [
      '.is-selected img',
      '.slick-current img',
      '.slick-active img',
      '[aria-hidden="false"] img',
      '.product__media-item--active img',
      '.is-active img',
      '[data-active="true"] img'
    ];
    
    for (let i = 0; i < activeSelectors.length; i++) {
      const activeImg = slider.querySelector(activeSelectors[i]);
      if (activeImg) {
        console.log('Found active image via selector:', activeSelectors[i]);
        return activeImg;
      }
    }
    
    // Method 2: Find image closest to center of viewport
    const images = slider.querySelectorAll('img[src*="mockup"]');
    let closestImage = null;
    let closestDistance = Infinity;
    
    images.forEach(function(img) {
      const rect = img.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const viewportCenterX = window.innerWidth / 2;
      const distance = Math.abs(centerX - viewportCenterX);
      
      if (distance < closestDistance && rect.width > 50) {
        closestDistance = distance;
        closestImage = img;
      }
    });
    
    if (closestImage) {
      console.log('Found active image via position (closest to center)');
      return closestImage;
    }
    
    return null;
  }
  
  /**
   * Get product variants from the page
   */
  function getProductVariants() {
    // Method 1: Try to find variants in a script tag
    const scriptTags = document.querySelectorAll('script[type="application/json"]');
    
    for (let i = 0; i < scriptTags.length; i++) {
      try {
        const data = JSON.parse(scriptTags[i].textContent);
        if (data && Array.isArray(data) && data.length > 0 && data[0].id) {
          return data;
        }
      } catch (e) {
        // Keep trying other scripts
      }
    }
    
    // Method 2: Try window.product or other common places
    if (window.product && window.product.variants) {
      return window.product.variants;
    }
    
    // Method 3: Parse from the page content
    const pageHTML = document.documentElement.outerHTML;
    const variantMatch = pageHTML.match(/\{"id":\d+,"title":"[^"]+","option1"/);
    
    if (variantMatch) {
      try {
        const startIndex = pageHTML.indexOf('[{', pageHTML.indexOf(variantMatch[0]) - 100);
        const endIndex = pageHTML.indexOf('}]', startIndex) + 2;
        const jsonStr = pageHTML.substring(startIndex, endIndex);
        return JSON.parse(jsonStr);
      } catch (e) {
        console.log('Could not parse variants from page');
      }
    }
    
    return null;
  }
  
  /**
   * Find all clickable thumbnail images
   */
  function findThumbnailImages() {
    const selectors = [
      '.product-single__thumbnails img',
      '.product__media-thumbnail img',
      '.product-single__thumbnail img',
      '[data-thumbnail] img',
      '.product__thumbnail img',
      '.product-photos__item--thumb img',
      'a[href*="cdn.shopify.com"] img'
    ];
    
    for (let i = 0; i < selectors.length; i++) {
      const images = document.querySelectorAll(selectors[i]);
      if (images.length > 0) {
        return Array.from(images);
      }
    }
    
    // Fallback: find all small product images
    const allImages = document.querySelectorAll('img[src*="mockup"]');
    return Array.from(allImages).filter(function(img) {
      const width = img.width || img.naturalWidth;
      return width > 50 && width < 400;
    });
  }
  
  /**
   * Handle interaction with an image (click or swipe)
   */
  function handleImageInteraction(image, variants, interactionType) {
    const imgSrc = image.src || image.getAttribute('src');
    
    if (!imgSrc) {
      console.log('No image source found');
      return;
    }
    
    console.log(`${interactionType === 'click' ? '🖱️' : '📱'} ${interactionType}: `, imgSrc);
    
    // Extract the unique part of the filename
    const filenameMatch = imgSrc.match(/mockup-([a-f0-9]+)/);
    
    if (!filenameMatch) {
      console.log('Could not extract filename from image');
      return;
    }
    
    const imageId = filenameMatch[1];
    console.log('🔍 Looking for variant with image ID:', imageId);
    
    // Find the variant that has this image
    const matchedVariant = variants.find(function(variant) {
      if (!variant.featured_image) return false;
      const variantImgSrc = variant.featured_image.src;
      return variantImgSrc && variantImgSrc.indexOf(imageId) !== -1;
    });
    
    if (!matchedVariant) {
      console.log('❌ No matching variant found for this image');
      return;
    }
    
    console.log('✅ Found matching variant:', matchedVariant.option1);
    
    // Select the color option
    selectColorOption(matchedVariant.option1);
  }
  
  /**
   * Select the color in the dropdown/selector
   */
  function selectColorOption(colorValue) {
    console.log('🎨 Selecting color:', colorValue);
    
    // Method 1: Dropdown select
    const selectElement = document.querySelector('select[name="Colour"], select[name="Color"], select[data-option="Colour"]');
    if (selectElement) {
      if (selectElement.value !== colorValue) {
        selectElement.value = colorValue;
        selectElement.dispatchEvent(new Event('change', { bubbles: true }));
        console.log('✅ Selected via dropdown');
        return;
      } else {
        console.log('ℹ️ Color already selected');
        return;
      }
    }
    
    // Method 2: Radio buttons
    const radioButton = document.querySelector(`input[type="radio"][value="${colorValue}"]`);
    if (radioButton && !radioButton.checked) {
      radioButton.checked = true;
      radioButton.dispatchEvent(new Event('change', { bubbles: true }));
      console.log('✅ Selected via radio button');
      return;
    }
    
    // Method 3: Swatch/button click
    const swatchButton = document.querySelector(`[data-value="${colorValue}"]`);
    if (swatchButton) {
      swatchButton.click();
      console.log('✅ Selected via swatch click');
      return;
    }
    
    // Method 4: Try finding by label text
    const labels = document.querySelectorAll('label');
    for (let i = 0; i < labels.length; i++) {
      if (labels[i].textContent.trim() === colorValue) {
        labels[i].click();
        console.log('✅ Selected via label click');
        return;
      }
    }
    
    console.log('❌ Could not find color selector for:', colorValue);
  }
  
})();